import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useLogoReset from '../../hooks/useLogoReset';
import useClearSelectionOnFilterChange from '../../hooks/useClearSelectionOnFilterChange';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import CustomerFuzzyMatchDialog from './components/CustomerFuzzyMatchDialog';
import { mergeWhitelistWithDefault, mergeColumnsWithDefault } from '../../utils/mergePrefsWithDefaults';
import {
  Plus, Loader2, ArrowUpFromLine, RefreshCw, Calendar, X,
  Upload, FileText, Sparkles, Truck, Check, Copy, ListOrdered, Settings2
} from 'lucide-react';
import { ColumnSettingsModal } from './ColumnSettingsModal';
import { EditExportSheet } from './EditExportSheet';
import { ExportDispatchScheduleTab } from './ExportDispatchScheduleTab';
import { useBulkSelection, useColumnManagement, useInlineEdit, useInAppSearchShortcut } from '../../hooks';

// Extracted reusable components
import { 
  BulkStatusModal, 
  ExportsFilterBar,
  ExportsTable,
  Pagination,
  AddCustomerModal,
  DeleteConfirmationModal,
  StatusColorLegend,
  // Form sections for Add/Edit Export Modal
  AIUploadSection,
  BookingInfoSection,
  CustomerShipperSection,
  PickupLocationSection,
  TerminalEquipmentSection,
  DestinationSection,
  ImportantDatesSection,
  AssignmentCargoSection,
  MultipleStopSection,
  AdditionalInfoSection,
  FeesSection,
  RemarksSection
} from './components';
import { LocationChangeModal } from './LocationChangeModal';
import { StatusOrderModal } from './StatusOrderModal';
import { StatusColorSettings } from './StatusColorSettings';

// Protected columns that cannot be dragged
const PROTECTED_COLUMNS = ['marker', 'actions'];

// Export statuses - similar to imports but with export-specific statuses
const EXPORT_STATUSES = [
  'Empty in Customer',
  'Available for Pickup',
  'Need Appt',
  'On the way to Customer',
  'Important Customer',
  'Delivered to Customer',
  'In Yard',
  'Container Returned',
  'On the way to Return',
  'Delivered to Exam Location',
  'New Shipment',
  'Empty Container in Yard',
  'On the way to Pickup',
  'Shipment Closed',
  'Trouble'
];

const STATUS_COLORS = {
  'Empty in Customer': 'status-empty-in-customer',
  'Available for Pickup': 'status-available-for-pickup',
  'Need Appt': 'status-need-appt',
  'On the way to Customer': 'status-on-way-to-customer',
  'Important Customer': 'status-important-customer',
  'Delivered to Customer': 'status-delivered-to-customer',
  'In Yard': 'status-in-yard',
  'Container Returned': 'status-container-returned',
  'On the way to Return': 'status-on-way-to-return',
  'Delivered to Exam Location': 'status-delivered-to-exam',
  'New Shipment': 'status-new-shipment',
  'Empty Container in Yard': 'status-empty-container-yard',
  'On the way to Pickup': 'status-on-way-to-pickup',
  'Shipment Closed': 'status-shipment-closed',
  'Trouble': 'status-trouble'
};

// Default row background colors based on status (for inline row styling)
const DEFAULT_STATUS_ROW_COLORS = {
  // Import statuses (shared)
  'Empty in Customer': '#FFDAB9',        // Light Orange/Peach
  'Available for Pickup': '#90EE90',     // Light Green
  'Available for Pick Up': '#90EE90',    // Light Green (alternate spelling)
  'Need Appt': '#1a1a1a',                // Black/Dark (text will be white)
  'On the way to Customer': '#FFB6C1',   // Light Pink
  'Important Customer': '#A0522D',       // Brown/Sienna
  'Delivered to Customer': '#FFC0CB',    // Pink
  'In Yard': '#DDA0DD',                  // Purple/Plum
  'Container Returned': '#D3D3D3',       // Light Gray
  'On the way to Return': '#AFEEEE',     // Cyan/Pale Turquoise
  'Delivered to Exam Location': '#8B4513', // Brown/Saddle Brown
  'New Shipment': '#E6E6FA',             // Lavender (light default)
  'Empty Container in Yard': '#FFFFE0',  // Light Yellow
  'On the way to Pickup': '#228B22',     // Dark Green/Forest Green
  'On the way to pick up': '#228B22',    // Dark Green (alternate spelling)
  'Shipment Closed': '#ADD8E6',          // Light Blue
  'Trouble': '#FA8072',                  // Salmon/Light Coral
  // Export-specific statuses
  'New Booking': '#E6E6FA',              // Lavender
  'Empty Dispatched': '#FFE4B5',         // Moccasin
  'Empty Picked Up': '#FFDAB9',          // Peach
  'At Shipper': '#DDA0DD',               // Plum
  'Loaded': '#ADD8E6',                   // Light Blue
  'En Route to Port': '#AFEEEE',         // Pale Turquoise
  'At Port': '#98FB98',                  // Pale Green
  'On Vessel': '#90EE90',                // Light Green
  'Completed': '#D3D3D3',                // Light Gray
  'Cancelled': '#FFA07A'                 // Light Salmon
};

// Default columns for exports table
const DEFAULT_COLUMNS = [
  { id: 'marker', label: '', visible: true, width: 6 },
  { id: 'important', label: '', visible: true, width: 24 },
  { id: 'actions', label: '', visible: true, width: 60 },
  { id: 'booking_number', label: 'BK#', visible: true, width: 80 },
  { id: 'mbl_number', label: 'MBL', visible: false, width: 80 },
  { id: 'container_number', label: 'Container', visible: true, width: 95 },
  { id: 'container_size', label: 'Sz', visible: true, width: 35 },
  { id: 'chassis', label: 'Chassis', visible: true, width: 60 },
  { id: 'overweight', label: 'OW', visible: true, width: 30 },
  { id: 'hazmat', label: 'HZ', visible: true, width: 30 },
  { id: 'ssl', label: 'SSL', visible: true, width: 45 },
  { id: 'pp_ctf', label: 'PP', visible: true, width: 40 },
  { id: 'vessel_name', label: 'Vessel', visible: false, width: 80 },
  { id: 'customer_name', label: 'Customer', visible: true, width: 80 },
  { id: 'destination_port', label: 'Dest', visible: true, width: 55 },
  { id: 'terminal', label: 'Terminal', visible: true, width: 60 },
  { id: 'return_terminal', label: 'Ret', visible: true, width: 50 },
  { id: 'yard', label: 'Yard', visible: true, width: 55 },
  { id: 'cutoff_date', label: 'Cutoff', visible: true, width: 55 },
  { id: 'erd', label: 'ERD', visible: true, width: 50 },
  { id: 'load_rdy', label: 'LoadRdy', visible: true, width: 55 },
  { id: 'load_return', label: 'Load Ret', visible: true, width: 60 },
  { id: 'status', label: 'Status', visible: true, width: 90 },
  { id: 'driver_name', label: 'Driver', visible: true, width: 60 },
  { id: 'pickup_address', label: 'Pickup', visible: false, width: 100 },
  { id: 'docs', label: 'Doc', visible: true, width: 35 },
  { id: 'remarks', label: 'Remark', visible: true, width: 70 }
];

const getInitialFormState = () => ({
  booking_number: '',
  mbl_number: '',
  container_number: '',
  container_size: '',
  ssl_id: '',
  ssl: '',
  vessel_name: '',
  customer_id: '',
  customer_name: '',
  address_id: '',
  shipper_name: '',
  pickup_address: '',
  pickup_city: '',
  pickup_state: '',
  pickup_zip: '',
  pickup_contact: '',
  pickup_phone: '',
  pickup_terminal_id: '',
  return_terminal_id: '',
  destination_port: '',
  destination_terminal: '',
  yard_id: '',
  chassis_type: '',
  chassis_number: '',
  cutoff_date: '',
  erd: '',
  load_rdy: '',
  load_return: '',
  driver_id: '',
  driver_name: '',
  weight: 0,
  commodity: '',
  // Fees
  pier_pass: 0,
  pier_pass_paid: false,
  ctf_charge: 0,
  ctf_paid: false,
  fees_paid_by: '',
  overweight_fee: 0,
  is_overweight: false,
  is_hazmat: false,
  // Additional fields like Import
  is_multiple_stop: false,
  secondary_address: '',
  secondary_city: '',
  secondary_state: '',
  secondary_zip: '',
  secondary_contact: '',
  secondary_phone: '',
  additional_client_info: '',
  forwarder_reference: '',
  is_important: false,
  remarks: '',
  special_instructions: '',
  status: 'New Shipment'
});

export const ExportsDashboard = ({ masterData = {}, customers: initialCustomers = [] }) => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  // Hijack Ctrl/Cmd+F → in-app search (server-backed, full dataset).
  useInAppSearchShortcut('[data-testid="export-search-input"]');
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sslFilter, setSslFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [hiddenStatuses, setHiddenStatuses] = useState([]);
  // iter 265.3 — Row Selection Persistence preference (shared across dashboards)
  const [filterClearSelection, setFilterClearSelection] = useState(false);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalExports, setTotalExports] = useState(0);
  const [pageSize] = useState(50);  // Default page size
  
  // Local customers state to allow updates without parent refresh
  const [customers, setCustomers] = useState(initialCustomers);
  
  // Update local customers when prop changes
  useEffect(() => {
    setCustomers(initialCustomers);
  }, [initialCustomers]);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExport, setEditingExport] = useState(null);
  const [formData, setFormData] = useState(getInitialFormState());
  const [saving, setSaving] = useState(false);
  
  // Add Customer Modal
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  // iter 264d.15b — fuzzy-match dedup dialog state for the Exports flow
  const [exportsFuzzyOpen, setExportsFuzzyOpen] = useState(false);
  const [exportsFuzzyMatches, setExportsFuzzyMatches] = useState([]);
  const [addingCustomer, setAddingCustomer] = useState(false);

  // Recently used customers (last 10 unique) — populated when Add modal opens
  const [recentCustomerIds, setRecentCustomerIds] = useState([]);
  useEffect(() => {
    if (showAddModal || showEditModal) {
      api.get('/shipments/recent-customers')
        .then(res => setRecentCustomerIds(res.data || []))
        .catch(() => {});
    }
  }, [showAddModal, showEditModal, api]);

  // Delete Export Modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteExport, setDeleteExport] = useState(null);
  const [deletingExport, setDeletingExport] = useState(false);

  // Add Address Modal (inline like Import form)
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    contact_name: '',
    contact_phone: '',
    receiving_hours_from: '',
    receiving_hours_to: '',
    remark: '',
    is_default: false
  });
  const [addingAddress, setAddingAddress] = useState(false);

  // AI DO Upload
  const [uploadingDO, setUploadingDO] = useState(false);
  const [uploadedDOFile, setUploadedDOFile] = useState(null);
  const doFileInputRef = useRef(null);

  // Column settings and customization
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [statusColors, setStatusColors] = useState(DEFAULT_STATUS_ROW_COLORS);
  const [statusOrderModalOpen, setStatusOrderModalOpen] = useState(false);
  const [statusOrder, setStatusOrder] = useState(EXPORT_STATUSES);
  const [colorSettingsOpen, setColorSettingsOpen] = useState(false);
  
  // Inline editing - track last edited for highlight
  const [lastEditedExportId, setLastEditedExportId] = useState(null);
  
  // Bulk action modal states
  const [bulkStatusModalOpen, setBulkStatusModalOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  const tableRef = useRef(null);

  // Dispatch modal state
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [dispatchingExport, setDispatchingExport] = useState(null);
  const [dispatchForm, setDispatchForm] = useState({
    driver_id: '',
    pickup_date: '',
    pickup_time: '',
    delivery_date: '',
    delivery_time: '',
    instructions: '',
    task_type: 'LOAD_OUT_DELIVER'
  });
  const [sendingDispatch, setSendingDispatch] = useState(false);

  // Location/Status change modal
  const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
  const [statusChangeExport, setStatusChangeExport] = useState(null);
  const [newStatusValue, setNewStatusValue] = useState('');
  const [statusChangeLocation, setStatusChangeLocation] = useState('');
  const [savingStatusChange, setSavingStatusChange] = useState(false);

  // Get SSLs from masterData
  const ssls = masterData?.ssls || [];

  const fetchExports = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      // Build query params with pagination and filters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', pageSize.toString());
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (sslFilter) params.append('ssl_id', sslFilter);
      if (driverFilter) params.append('driver_id', driverFilter);
      if (customerFilter) params.append('customer_id', customerFilter);
      if (includeClosed) params.append('include_closed', 'true');
      if (hiddenStatuses.length > 0) params.append('hidden_statuses', hiddenStatuses.join(','));
      
      const response = await api.get(`/exports?${params.toString()}`);
      
      // Handle paginated response
      if (response.data?.exports) {
        setExports(response.data.exports);
        setTotalExports(response.data.total || 0);
        setTotalPages(response.data.pages || 1);
        setCurrentPage(response.data.page || 1);
      } else if (Array.isArray(response.data)) {
        // Legacy format fallback
        setExports(response.data);
        setTotalExports(response.data.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Failed to fetch exports:', error);
      setExports([]);
    } finally {
      setLoading(false);
    }
  }, [api, searchTerm, statusFilter, sslFilter, driverFilter, customerFilter, includeClosed, hiddenStatuses, pageSize]);

  // Exports are now filtered server-side - just use them directly
  // Client-side filtering only for any additional filters not sent to server
  const filteredExportsBase = exports;

  // Handle column header click for sorting
  const handleColumnSort = (columnId) => {
    if (sortColumn === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  const clearSort = () => {
    setSortColumn(null);
    setSortDirection('asc');
  };

  // Sort exports based on column selection
  const filteredExports = [...filteredExportsBase].sort((a, b) => {
    if (!sortColumn) return 0; // No sorting, keep original order
    
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];
    
    // Handle null/undefined
    if (aVal === null || aVal === undefined || aVal === '') aVal = sortDirection === 'asc' ? 'zzzzz' : '';
    if (bVal === null || bVal === undefined || bVal === '') bVal = sortDirection === 'asc' ? 'zzzzz' : '';
    
    // Handle dates
    if (sortColumn.includes('date') || sortColumn === 'cutoff_date' || sortColumn === 'erd') {
      aVal = aVal ? new Date(aVal).getTime() : (sortDirection === 'asc' ? Infinity : 0);
      bVal = bVal ? new Date(bVal).getTime() : (sortDirection === 'asc' ? Infinity : 0);
    }
    
    // String comparison
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Bulk selection using shared hook
  const {
    selectedIds: selectedExports,
    selectedCount,
    toggleSelection: toggleSelectExport,
    toggleSelectAll,
    isSelected,
    isAllSelected,
    isSomeSelected,
    deselectAll
  } = useBulkSelection({
    items: filteredExports,
    idField: 'id'
  });

  // iter 265.2 — Reset filters + selection when the Drayly logo is clicked.
  useLogoReset(() => {
    setSearchTerm('');
    setStatusFilter('');
    setSslFilter('');
    setDriverFilter('');
    setCustomerFilter('');
    setIncludeClosed(false);
    setHiddenStatuses([]);
    try { deselectAll?.(); } catch (_) { /* ignore */ }
  });

  // iter 265.3 — Auto-clear bulk selection when a filter changes (opt-in).
  useClearSelectionOnFilterChange({
    enabled: filterClearSelection,
    deselectAll,
    deps: [statusFilter, sslFilter, driverFilter, customerFilter, searchTerm, includeClosed, JSON.stringify(hiddenStatuses)],
  });

  // Inline edit using shared hook
  const {
    editCell: inlineEditCell,
    editValue: inlineEditValue,
    setEditValue: setInlineEditValue,
    saving: savingInlineEdit,
    inputRef: inlineEditInputRef,
    startEdit: startInlineEdit,
    cancelEdit: cancelInlineEdit,
    saveEdit: handleInlineEditSave,
    handleKeyDown: handleInlineEditKeyDown,
    isEditing: isInlineEditing
  } = useInlineEdit({
    onSave: async (exportId, field, value) => {
      await api.put(`/exports/${exportId}`, { [field]: value || null });
      setExports(prev => prev.map(exp => 
        exp.id === exportId ? { ...exp, [field]: value } : exp
      ));
      setLastEditedExportId(exportId);
      setTimeout(() => setLastEditedExportId(null), 3000);
      toast.success('Updated successfully');
    }
  });

  // Column management using shared hook with custom API pattern
  const {
    columns,
    setColumns,
    visibleColumns,
    columnsChanged,
    savingColumns,
    draggedColumn,
    dragOverColumn,
    resizingColumn,
    loadColumnPreferences,
    saveColumnPreferences,
    toggleColumnVisibility,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleResizeStart,
    resetToDefaults
  } = useColumnManagement({
    defaultColumns: DEFAULT_COLUMNS,
    api,
    protectedColumns: PROTECTED_COLUMNS,
    customLoadFn: async () => {
      try {
        const response = await api.get('/user/preferences/columns?dashboard=exports');
        if (response.data?.columns && response.data.columns.length > 0) {
          // iter 264d.18 — shared helper (see ShipmentDashboard for same
          // fix). Preserves saved drag-drop order + slots new columns.
          return mergeColumnsWithDefault(response.data.columns, DEFAULT_COLUMNS);
        }
        return null;
      } catch (error) {
        console.error('Failed to load column preferences:', error);
        return null;
      }
    },
    customSaveFn: async (columnsToSave) => {
      await api.post('/user/preferences/columns', { 
        columns: columnsToSave,
        dashboard: 'exports'
      });
      toast.success('Column settings saved');
    }
  });

  // Load data on mount
  useEffect(() => {
    fetchExports(1);
    loadColumnPreferences();
    // Load saved status colors and order
    (async () => {
      try {
        const resp = await api.get('/user/preferences');
        if (resp.data) {
          if (resp.data.export_status_colors) {
            setStatusColors({ ...DEFAULT_STATUS_ROW_COLORS, ...resp.data.export_status_colors });
          }
          if (resp.data.export_status_order) {
            // iter 264d.17 — merge with EXPORT_STATUSES so new statuses added
            // after the user saved their order still appear at their default
            // slot rather than falling to position 999.
            setStatusOrder(mergeWhitelistWithDefault(resp.data.export_status_order, EXPORT_STATUSES));
          }
          if (typeof resp.data.filter_clear_selection === 'boolean') {
            setFilterClearSelection(resp.data.filter_clear_selection);
          }
        }
      } catch (e) { /* ignore - use defaults */ }
    })();
  }, []); // Only run on mount
  
  // Refetch when filters change (with debounce for search)
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchExports(1);  // Reset to page 1 when filters change
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, statusFilter, includeClosed, hiddenStatuses]);
  
  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchExports(newPage);
    }
  };

  // Get row background color based on status
  const getRowBackgroundColor = (status) => {
    // Try exact match first
    let color = statusColors[status] || DEFAULT_STATUS_ROW_COLORS[status];
    
    // If no exact match, try case-insensitive match
    if (!color && status) {
      const statusLower = status.toLowerCase();
      const matchKey = Object.keys(DEFAULT_STATUS_ROW_COLORS).find(
        key => key.toLowerCase() === statusLower
      );
      if (matchKey) {
        color = DEFAULT_STATUS_ROW_COLORS[matchKey];
      }
    }
    
    if (!color) return {};
    return {
      backgroundColor: color,
      color: color === '#1a1a1a' ? 'white' : 'black'
    };
  };

  // Render inline editable cell using the hook
  const renderEditableCell = (exp, field, displayValue) => {
    const editing = isInlineEditing(exp.id, field);
    
    if (editing) {
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inlineEditInputRef}
            value={inlineEditValue}
            onChange={(e) => setInlineEditValue(e.target.value)}
            onKeyDown={handleInlineEditKeyDown}
            className="h-7 w-32 text-xs bg-slate-800 border-blue-500"
            onClick={(e) => e.stopPropagation()}
          />
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); handleInlineEditSave(); }} disabled={savingInlineEdit}>
            <Check className="h-3 w-3 text-green-400" />
          </Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); cancelInlineEdit(); }}>
            <X className="h-3 w-3 text-red-400" />
          </Button>
        </div>
      );
    }
    
    return (
      <span 
        onClick={() => startInlineEdit(exp.id, field, exp[field])}
        className="cursor-pointer hover:bg-slate-700/50 px-1 py-0.5 rounded min-w-[40px] inline-block"
        title="Click to edit"
      >
        {displayValue || '-'}
      </span>
    );
  };

  // Handle inline status change
  const handleStatusChange = async (exportId, newStatus) => {
    try {
      await api.put(`/exports/${exportId}`, { status: newStatus });
      setExports(prev => 
        prev.map(exp => 
          exp.id === exportId ? { ...exp, status: newStatus } : exp
        )
      );
      setLastEditedExportId(exportId);
      setTimeout(() => setLastEditedExportId(null), 3000);
      toast.success(`Status updated to "${newStatus}"`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Handle bulk status change
  const handleBulkStatusChange = async () => {
    if (!bulkStatus || selectedCount === 0) return;
    setBulkUpdating(true);
    try {
      const promises = Array.from(selectedExports).map(id =>
        api.put(`/exports/${id}`, { status: bulkStatus })
      );
      await Promise.all(promises);
      setExports(prev =>
        prev.map(exp =>
          isSelected(exp.id) ? { ...exp, status: bulkStatus } : exp
        )
      );
      toast.success(`Updated ${selectedCount} exports to "${bulkStatus}"`);
      deselectAll();
      setBulkStatusModalOpen(false);
      setBulkStatus('');
    } catch (error) {
      toast.error('Failed to update some exports');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Toggle important flag
  const toggleImportant = async (exportId, currentValue) => {
    try {
      await api.put(`/exports/${exportId}`, { is_important: !currentValue });
      setExports(prev =>
        prev.map(exp =>
          exp.id === exportId ? { ...exp, is_important: !currentValue } : exp
        )
      );
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Copy container/booking numbers (selected or all visible)
  const copyContainerNumbers = () => {
    let numbers;
    if (selectedCount > 0) {
      numbers = exports
        .filter(e => isSelected(e.id))
        .map(e => e.container_number || e.booking_number || '')
        .filter(Boolean)
        .join('\n');
      toast.success(`Copied ${selectedCount} selected container numbers`);
    } else {
      numbers = exports
        .map(e => e.container_number || e.booking_number || '')
        .filter(Boolean)
        .join('\n');
      toast.success(`Copied ${exports.length} container numbers`);
    }
    navigator.clipboard.writeText(numbers).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  // Save status order to user preferences
  const saveStatusOrder = async (newOrder) => {
    try {
      await api.put('/user/preferences', { export_status_order: newOrder });
      setStatusOrder(newOrder);
      setStatusOrderModalOpen(false);
      toast.success('Status order saved');
    } catch (error) {
      toast.error('Failed to save status order');
    }
  };

  // Open dispatch modal
  const handleOpenDispatch = (exp) => {
    setDispatchingExport(exp);
    setDispatchForm({
      driver_id: exp.driver_id || '',
      pickup_date: exp.load_rdy || '',
      pickup_time: '',
      delivery_date: exp.cutoff_date || '',
      delivery_time: '',
      instructions: exp.remarks || '',
      task_type: 'LOAD_OUT_DELIVER'
    });
    setDispatchModalOpen(true);
  };

  // Send dispatch
  const handleSendDispatch = async () => {
    if (!dispatchingExport) return;
    setSendingDispatch(true);
    try {
      // Update export with dispatch info
      const updateData = {
        driver_id: dispatchForm.driver_id,
        status: 'New Shipment',
        dispatched_at: new Date().toISOString(),
        dispatch_instructions: dispatchForm.instructions
      };
      
      if (dispatchForm.driver_id) {
        const driver = masterData?.drivers?.find(d => d.id === dispatchForm.driver_id);
        if (driver) {
          updateData.driver_name = driver.name;
        }
      }
      
      await api.put(`/exports/${dispatchingExport.id}`, updateData);
      
      // Update local state
      setExports(prev =>
        prev.map(exp =>
          exp.id === dispatchingExport.id ? { ...exp, ...updateData } : exp
        )
      );
      
      toast.success('Dispatch sent successfully');
      setDispatchModalOpen(false);
      setDispatchingExport(null);
    } catch (error) {
      toast.error('Failed to send dispatch');
    } finally {
      setSendingDispatch(false);
    }
  };

  // Open status change modal
  const handleOpenStatusChange = (exp) => {
    setStatusChangeExport(exp);
    setNewStatusValue(exp.status || 'New Shipment');
    setStatusChangeLocation(exp.current_location || '');
    setStatusChangeModalOpen(true);
  };

  // Save status change
  const handleSaveStatusChange = async () => {
    if (!statusChangeExport) return;
    setSavingStatusChange(true);
    try {
      const updateData = {
        status: newStatusValue,
        current_location: statusChangeLocation,
        driver_id: statusChangeExport.driver_id || null,
        driver_name: statusChangeExport.driver_name || null
      };
      
      await api.put(`/exports/${statusChangeExport.id}`, updateData);
      
      setExports(prev =>
        prev.map(exp =>
          exp.id === statusChangeExport.id ? { ...exp, ...updateData } : exp
        )
      );
      
      toast.success('Status updated successfully');
      setStatusChangeModalOpen(false);
      setStatusChangeExport(null);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSavingStatusChange(false);
    }
  };

  // Handle cell color change (same as Import)
  const handleCellColorChange = async (exportId, columnKey, color) => {
    try {
      await api.put(`/exports/${exportId}/cell-color`, {
        column_key: columnKey,
        color: color
      });
      
      // Update local state
      setExports(prev => prev.map(exp => {
        if (exp.id === exportId) {
          const cell_colors = { ...(exp.cell_colors || {}) };
          if (color) {
            cell_colors[columnKey] = color;
          } else {
            delete cell_colors[columnKey];
          }
          return { ...exp, cell_colors };
        }
        return exp;
      }));
      
      toast.success('Color updated');
    } catch (error) {
      toast.error('Failed to update color');
    }
  };

  // Handle inline date/time save for cutoff, load_rdy, etc.
  const handleInlineDateSave = async (exportId, field, value) => {
    try {
      const updatePayload = { [field]: value };
      
      // When saving a terminal/yard ID, also resolve and save the name
      if (field === 'pickup_terminal_id') {
        const terminal = masterData.terminals?.find(t => t.id === value);
        updatePayload.pickup_terminal_name = terminal?.name || '';
      } else if (field === 'return_terminal_id') {
        const terminal = masterData.terminals?.find(t => t.id === value);
        updatePayload.return_terminal_name = terminal?.name || '';
      } else if (field === 'yard_id') {
        const yard = masterData.yards?.find(y => y.id === value);
        updatePayload.yard_name = yard?.name || '';
      }
      
      await api.put(`/exports/${exportId}`, updatePayload);
      
      // Update local state
      setExports(prev => prev.map(exp => {
        if (exp.id === exportId) {
          return { ...exp, ...updatePayload };
        }
        return exp;
      }));
      
      toast.success(`${field.replace(/_/g, ' ')} updated`);
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Failed to update');
    }
  };

  // Handle chassis inline save
  const handleChassisSave = async (exportId, chassisType, chassisNumber) => {
    try {
      await api.put(`/exports/${exportId}`, {
        chassis_type: chassisType,
        chassis_number: chassisNumber
      });
      
      // Update local state
      setExports(prev => prev.map(exp => {
        if (exp.id === exportId) {
          return { ...exp, chassis_type: chassisType, chassis_number: chassisNumber };
        }
        return exp;
      }));
      
      toast.success('Chassis updated');
    } catch (error) {
      console.error('Failed to update chassis:', error);
      toast.error('Failed to update chassis');
    }
  };

  // Handle PP/CTF inline save
  const handlePPCTFSave = async (exportId, ppctfData) => {
    try {
      await api.put(`/exports/${exportId}`, ppctfData);
      
      // Update local state
      setExports(prev => prev.map(exp => {
        if (exp.id === exportId) {
          return { ...exp, ...ppctfData };
        }
        return exp;
      }));
      
      toast.success('PP/CTF updated');
    } catch (error) {
      console.error('Failed to update PP/CTF:', error);
      toast.error('Failed to update PP/CTF');
    }
  };

  // Get cell text color based on saved color or default
  const getCellTextColor = (color) => {
    return color || '#cbd5e1'; // Default slate-300
  };

  // Auto-fill pickup address when customer is selected (similar to Import form)
  useEffect(() => {
    if (formData.customer_id && customers.length > 0) {
      const customer = customers.find(c => c.id === formData.customer_id);
      if (customer && customer.addresses && customer.addresses.length > 0) {
        // Only auto-fill if address is not already set
        if (!formData.pickup_address) {
          const defaultAddress = customer.addresses.find(a => a.is_default) || customer.addresses[0];
          if (defaultAddress) {
            console.log('useEffect: Auto-filling address from customer:', customer.name, defaultAddress);
            setFormData(prev => ({
              ...prev,
              address_id: defaultAddress.id || '',
              pickup_address: defaultAddress.address || '',
              pickup_city: defaultAddress.city || '',
              pickup_state: defaultAddress.state || '',
              pickup_zip: defaultAddress.zip_code || defaultAddress.zip || '',
              pickup_contact: defaultAddress.contact_name || '',
              pickup_phone: defaultAddress.contact_phone || ''
            }));
          }
        }
      }
    }
  }, [formData.customer_id, customers]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-populate booking_number from mbl_number if booking is empty
      if (field === 'mbl_number' && !prev.booking_number) {
        newData.booking_number = value;
      }
      
      // When driver is selected from dropdown, also set driver_name
      if (field === 'driver_id') {
        const driver = masterData.drivers?.find(d => d.id === value);
        if (driver) {
          newData.driver_name = driver.name;
        }
      }
      
      return newData;
    });
  };

  const handleSaveExport = async () => {
    if (!formData.booking_number && !formData.mbl_number) {
      toast.error('BK# or MBL# is required');
      return;
    }

    setSaving(true);
    try {
      // Sanitize numeric fields - convert empty strings to 0
      const sanitizedData = {
        ...formData,
        weight: parseFloat(formData.weight) || 0,
        pier_pass: parseFloat(formData.pier_pass) || 0,
        ctf_charge: parseFloat(formData.ctf_charge) || 0,
        overweight_fee: parseFloat(formData.overweight_fee) || 0,
        rate: parseFloat(formData.rate) || 0,
        chassis_fee: parseFloat(formData.chassis_fee) || 0,
        port_fee: parseFloat(formData.port_fee) || 0,
        pieces: parseInt(formData.pieces) || 0,
      };
      
      if (editingExport) {
        await api.put(`/exports/${editingExport.id}`, sanitizedData);
        toast.success('Export updated successfully');
      } else {
        await api.post('/exports', sanitizedData);
        toast.success('Export created successfully');
      }
      setShowAddModal(false);
      setShowEditModal(false);
      setFormData(getInitialFormState());
      setEditingExport(null);
      fetchExports();
    } catch (error) {
      // Handle Pydantic validation errors which return array of {type, loc, msg, input, url}
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to save export';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation error format
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEditExport = (exp) => {
    setEditingExport(exp);
    setFormData({
      booking_number: exp.booking_number || '',
      mbl_number: exp.mbl_number || '',
      container_number: exp.container_number || '',
      container_size: exp.container_size || '',
      ssl_id: exp.ssl_id || '',
      ssl: exp.ssl || '',
      vessel_name: exp.vessel_name || '',
      customer_id: exp.customer_id || '',
      customer_name: exp.customer_name || '',
      shipper_name: exp.shipper_name || '',
      pickup_address: exp.pickup_address || '',
      pickup_city: exp.pickup_city || '',
      pickup_state: exp.pickup_state || '',
      pickup_contact: exp.pickup_contact || '',
      pickup_phone: exp.pickup_phone || '',
      pickup_terminal_id: exp.pickup_terminal_id || '',
      return_terminal_id: exp.return_terminal_id || '',
      destination_port: exp.destination_port || '',
      destination_terminal: exp.destination_terminal || '',
      yard_id: exp.yard_id || '',
      chassis_type: exp.chassis_type || '',
      chassis_number: exp.chassis_number || '',
      cutoff_date: exp.cutoff_date || '',
      erd: exp.erd || '',
      load_rdy: exp.load_rdy || '',
      load_return: exp.load_return || '',
      driver_id: exp.driver_id || '',
      driver_name: exp.driver_name || '',
      weight: exp.weight || '',
      commodity: exp.commodity || '',
      // Fees
      pier_pass: exp.pier_pass || 0,
      pier_pass_paid: exp.pier_pass_paid || false,
      ctf_charge: exp.ctf_charge || 0,
      ctf_paid: exp.ctf_paid || false,
      fees_paid_by: exp.fees_paid_by || '',
      overweight_fee: exp.overweight_fee || 0,
      is_overweight: exp.is_overweight || false,
      is_hazmat: exp.is_hazmat || false,
      // Additional fields
      address_id: exp.address_id || '',
      pickup_zip: exp.pickup_zip || '',
      is_multiple_stop: exp.is_multiple_stop || false,
      secondary_address: exp.secondary_address || '',
      secondary_city: exp.secondary_city || '',
      secondary_state: exp.secondary_state || '',
      secondary_zip: exp.secondary_zip || '',
      secondary_contact: exp.secondary_contact || '',
      secondary_phone: exp.secondary_phone || '',
      additional_client_info: exp.additional_client_info || '',
      forwarder_reference: exp.forwarder_reference || '',
      is_important: exp.is_important || false,
      remarks: exp.remarks || '',
      special_instructions: exp.special_instructions || '',
      status: exp.status || 'New Shipment'
    });
    setShowEditModal(true);
  };

  const handleDeleteExport = async () => {
    if (!deleteExport) return;
    
    setDeletingExport(true);
    try {
      await api.delete(`/exports/${deleteExport.id}`);
      toast.success('Export deleted');
      setDeleteModalOpen(false);
      setDeleteExport(null);
      fetchExports();
    } catch (error) {
      toast.error('Failed to delete export');
    } finally {
      setDeletingExport(false);
    }
  };

  // Open delete confirmation
  const openDeleteModal = (exp) => {
    setDeleteExport(exp);
    setDeleteModalOpen(true);
  };

  // iter 264d.15b — Add new customer WITH fuzzy-match pre-check.
  const handleAddCustomer = async () => {
    if (!newCustomerName.trim()) {
      toast.error('Customer name is required');
      return;
    }

    setAddingCustomer(true);
    try {
      // Fuzzy-match first
      try {
        const fuzzy = await api.get(
          `/customers/fuzzy-match?name=${encodeURIComponent(newCustomerName.trim())}&threshold=0.85`
        );
        const matches = fuzzy.data?.matches || [];
        if (matches.length > 0) {
          setExportsFuzzyMatches(matches);
          setExportsFuzzyOpen(true);
          setAddingCustomer(false);
          return; // wait for Link / Create-Anyway
        }
      } catch (_e) { /* non-fatal */ }
      await _doAddCustomer(newCustomerName.trim());
    } finally {
      setAddingCustomer(false);
    }
  };

  const _doAddCustomer = async (nameOverride) => {
    try {
      const response = await api.post('/customers', { name: nameOverride || newCustomerName.trim() });
      toast.success('Customer added successfully');
      setFormData(prev => ({
        ...prev,
        customer_id: response.data.id,
        customer_name: response.data.name
      }));
      setNewCustomerName('');
      setShowAddCustomerModal(false);
      setExportsFuzzyOpen(false);
      setExportsFuzzyMatches([]);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to add customer';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    }
  };

  const handleLinkExportsCustomer = (existing) => {
    setFormData(prev => ({
      ...prev,
      customer_id: existing.id,
      customer_name: existing.name,
    }));
    setNewCustomerName('');
    setShowAddCustomerModal(false);
    setExportsFuzzyOpen(false);
    setExportsFuzzyMatches([]);
    toast.success(`Linked to existing customer "${existing.name}"`);
  };

  // Add new address to customer
  const handleAddAddress = async () => {
    if (!formData.customer_id) {
      toast.error('Please select a customer first');
      return;
    }
    if (!newAddress.address.trim() || !newAddress.city.trim() || !newAddress.state.trim()) {
      toast.error('Address, City, and State are required');
      return;
    }
    
    setAddingAddress(true);
    try {
      const response = await api.post(`/customers/${formData.customer_id}/addresses`, newAddress);
      toast.success('Address added successfully');
      
      // The API returns the full updated customer object, not just the address
      const updatedCustomer = response.data;
      
      // Get the newly added address (last one in the array)
      const addedAddress = updatedCustomer.addresses?.[updatedCustomer.addresses.length - 1];
      
      if (addedAddress) {
        // Update the form with the new address
        setFormData(prev => ({
          ...prev,
          address_id: addedAddress.id,
          pickup_address: addedAddress.address || '',
          pickup_city: addedAddress.city || '',
          pickup_state: addedAddress.state || '',
          pickup_zip: addedAddress.zip_code || addedAddress.zip || '',
          pickup_contact: addedAddress.contact_name || '',
          pickup_phone: addedAddress.contact_phone || ''
        }));
        
        // Update local customers state so the dropdown shows the new address
        setCustomers(prevCustomers => 
          prevCustomers.map(c => 
            c.id === formData.customer_id ? updatedCustomer : c
          )
        );
      }
      
      // Reset the new address form
      setNewAddress({
        label: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        contact_name: '',
        contact_phone: '',
        is_default: false
      });
      setShowAddAddressModal(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to add address';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setAddingAddress(false);
    }
  };

  // Handle customer selection - auto-fill pickup address from customer's default/first address
  const handleCustomerSelect = (customerId) => {
    if (customerId === 'add_new') {
      setShowAddCustomerModal(true);
      return;
    }
    
    const customer = customers.find(c => c.id === customerId);
    if (!customer) {
      console.error('Customer not found:', customerId);
      return;
    }
    
    // Find default address or first address
    let defaultAddress = null;
    if (customer.addresses && customer.addresses.length > 0) {
      defaultAddress = customer.addresses.find(a => a.is_default) || customer.addresses[0];
    }
    
    // Prepare the update
    const updates = { 
      customer_id: customerId, 
      customer_name: customer.name || '',
    };
    
    // Always set address fields (even if empty to clear old data)
    if (defaultAddress) {
      updates.address_id = defaultAddress.id || '';
      updates.pickup_address = defaultAddress.address || '';
      updates.pickup_city = defaultAddress.city || '';
      updates.pickup_state = defaultAddress.state || '';
      updates.pickup_zip = defaultAddress.zip_code || defaultAddress.zip || '';
      updates.pickup_contact = defaultAddress.contact_name || '';
      updates.pickup_phone = defaultAddress.contact_phone || '';
      
      toast.success(`✓ Address loaded: ${defaultAddress.address}, ${defaultAddress.city}, ${defaultAddress.state}`);
    } else {
      // Clear address fields if customer has no addresses
      updates.address_id = '';
      updates.pickup_address = '';
      updates.pickup_city = '';
      updates.pickup_state = '';
      updates.pickup_zip = '';
      updates.pickup_contact = '';
      updates.pickup_phone = '';
      
      toast.info(`Customer "${customer.name}" selected. No saved addresses - please enter pickup address manually.`);
    }
    
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Handle address selection from dropdown
  const handleAddressSelect = (addressId) => {
    const selectedCustomer = customers.find(c => c.id === formData.customer_id);
    const address = selectedCustomer?.addresses?.find(a => a.id === addressId);
    
    if (address) {
      setFormData(prev => ({
        ...prev,
        address_id: addressId,
        pickup_address: address.address || '',
        pickup_city: address.city || '',
        pickup_state: address.state || '',
        pickup_zip: address.zip_code || address.zip || '',
        pickup_contact: address.contact_name || '',
        pickup_phone: address.contact_phone || ''
      }));
    }
  };

  // Handle SSL selection
  const handleSSLSelect = (sslId) => {
    const ssl = ssls.find(s => s.id === sslId);
    setFormData(prev => ({ 
      ...prev, 
      ssl_id: sslId, 
      ssl: ssl?.name || ssl?.code || '' 
    }));
  };

  // Handle DO document upload for AI extraction
  const handleDOUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().match(/\.(pdf|png|jpg|jpeg)$/)) {
      toast.error('Please upload a PDF or image file (PNG, JPG)');
      return;
    }
    
    setUploadingDO(true);
    setUploadedDOFile(file);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('document_type', 'EXPORT');
      toast.info('AI is analyzing the document...', { duration: 5000 });
      
      const response = await api.post('/ai/extract-do', formDataUpload);
      
      if (response.data.success) {
        const extracted = response.data.data;
        
        setFormData(prev => ({
          ...prev,
          container_number: extracted.container_number || prev.container_number,
          container_size: extracted.container_size || prev.container_size,
          mbl_number: extracted.mbl || prev.mbl_number,
          booking_number: extracted.booking_number || prev.booking_number,
          customer_name: extracted.customer_name || prev.customer_name,
          shipper_name: extracted.shipper_name || prev.shipper_name,
          pickup_address: extracted.pickup_address || extracted.delivery_address || prev.pickup_address,
          pickup_city: extracted.pickup_city || extracted.delivery_city || prev.pickup_city,
          pickup_state: extracted.pickup_state || extracted.delivery_state || prev.pickup_state,
          pickup_zip: extracted.pickup_zip || extracted.delivery_zip || prev.pickup_zip,
          pickup_contact: extracted.pickup_contact || extracted.delivery_contact || prev.pickup_contact,
          pickup_phone: extracted.pickup_phone || extracted.delivery_phone || prev.pickup_phone,
          destination_port: extracted.destination_port || prev.destination_port,
          vessel_name: extracted.vessel_name || prev.vessel_name,
          voyage: extracted.voyage || prev.voyage,
          cutoff_date: extracted.cutoff_date || prev.cutoff_date,
          erd: extracted.erd || prev.erd,
          weight: extracted.weight_lbs || extracted.weight || prev.weight,
          commodity: extracted.commodity || prev.commodity,
          seal_number: extracted.seal_number || prev.seal_number,
          special_instructions: extracted.remarks ? 
            `${prev.special_instructions ? prev.special_instructions + '\n' : ''}${extracted.remarks}` : 
            prev.special_instructions
        }));
        
        // SSL matching from extracted ssl_name
        if (extracted.ssl_name && ssls.length > 0) {
          const sslName = extracted.ssl_name.toLowerCase();
          const matchedSSL = ssls.find(s => 
            (s.name || '').toLowerCase().includes(sslName) || 
            sslName.includes((s.name || '').toLowerCase()) ||
            (s.code || '').toLowerCase().includes(sslName)
          );
          if (matchedSSL) {
            setFormData(prev => ({ ...prev, ssl_id: matchedSSL.id, ssl: matchedSSL.name || matchedSSL.code }));
          }
        }
        
        // Return Terminal matching (for exports)
        if (extracted.return_terminal && masterData.terminals && masterData.terminals.length > 0) {
          const terminalName = extracted.return_terminal.toLowerCase();
          const matchedTerminal = masterData.terminals.find(t => 
            (t.name || '').toLowerCase().includes(terminalName) || 
            terminalName.includes((t.name || '').toLowerCase())
          );
          if (matchedTerminal) {
            setFormData(prev => ({ ...prev, return_terminal_id: matchedTerminal.id }));
          }
        }
        
        // Customer matching — use backend match first, then frontend fallback
        let matchedExportCustomer = null;
        if (extracted.matched_customer_id && customers.length > 0) {
          matchedExportCustomer = customers.find(c => c.id === extracted.matched_customer_id);
        }
        if (!matchedExportCustomer && extracted.customer_name && customers.length > 0) {
          const customerName = extracted.customer_name.toLowerCase();
          matchedExportCustomer = customers.find(c => 
            (c.name || '').toLowerCase().includes(customerName) || 
            customerName.includes((c.name || '').toLowerCase())
          );
        }
        if (matchedExportCustomer) {
          setFormData(prev => ({ ...prev, customer_id: matchedExportCustomer.id, customer_name: matchedExportCustomer.name }));
        } else if (extracted.customer_name) {
          toast.warning(
            `Customer "${extracted.customer_name}" not found. You can create them in Master Tables > Customers.`,
            { duration: 8000 }
          );
        }
        
        const fieldsExtracted = Object.values(extracted).filter(v => v !== null && v !== '').length;
        toast.success(`Document analyzed! ${fieldsExtracted} fields extracted`);
      } else {
        toast.error(response.data.error || 'Failed to extract data from document');
      }
    } catch (error) {
      console.error('DO extraction error:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to process document';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setUploadingDO(false);
      if (doFileInputRef.current) doFileInputRef.current.value = '';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string') {
      const trimmed = dateStr.trim();
      if (!trimmed || trimmed.toLowerCase() === 'none' || trimmed.toLowerCase() === 'null') {
        return '-';
      }
    }
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      return `${date.getMonth() + 1}/${date.getDate()}`;
    } catch (e) {
      return '-';
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    // Handle HH:MM format
    if (typeof timeStr === 'string' && timeStr.includes(':')) {
      const [hours, minutes] = timeStr.split(':');
      const h = parseInt(hours, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${minutes} ${ampm}`;
    }
    return timeStr;
  };

  // Count by status
  const statusCounts = EXPORT_STATUSES.reduce((acc, status) => {
    acc[status] = exports.filter(e => e.status === status).length;
    return acc;
  }, {});

  const closeModal = (isEdit) => {
    if (isEdit) {
      setShowEditModal(false);
    } else {
      setShowAddModal(false);
    }
    setFormData(getInitialFormState());
    setEditingExport(null);
    setUploadedDOFile(null);
  };

  // Render form content - shared between add and edit modals
  // Now uses extracted section components for better maintainability
  const renderFormContent = (isEdit = false) => (
    <div className="grid grid-cols-2 gap-4 py-4">
      {/* AI Document Upload - only show for new exports */}
      {!isEdit && (
        <AIUploadSection
          doFileInputRef={doFileInputRef}
          handleDOUpload={handleDOUpload}
          uploadingDO={uploadingDO}
          uploadedDOFile={uploadedDOFile}
          setUploadedDOFile={setUploadedDOFile}
        />
      )}

      {/* Booking Information */}
      <BookingInfoSection
        formData={formData}
        handleInputChange={handleInputChange}
        handleSSLSelect={handleSSLSelect}
        ssls={ssls}
        masterData={masterData}
      />

      {/* Customer & Shipper */}
      <CustomerShipperSection
        formData={formData}
        handleInputChange={handleInputChange}
        handleCustomerSelect={handleCustomerSelect}
        customers={customers}
        recentCustomerIds={recentCustomerIds}
      />

      {/* Pickup Location */}
      <PickupLocationSection
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        customers={customers}
        showAddAddressModal={showAddAddressModal}
        setShowAddAddressModal={setShowAddAddressModal}
        newAddress={newAddress}
        setNewAddress={setNewAddress}
        handleAddAddress={handleAddAddress}
        addingAddress={addingAddress}
      />

      {/* Terminal & Equipment */}
      <TerminalEquipmentSection
        formData={formData}
        handleInputChange={handleInputChange}
        masterData={masterData}
      />

      {/* Destination */}
      <DestinationSection
        formData={formData}
        handleInputChange={handleInputChange}
      />

      {/* Important Dates */}
      <ImportantDatesSection
        formData={formData}
        handleInputChange={handleInputChange}
      />

      {/* Assignment & Cargo */}
      <AssignmentCargoSection
        formData={formData}
        handleInputChange={handleInputChange}
        masterData={masterData}
        isEdit={isEdit}
        EXPORT_STATUSES={EXPORT_STATUSES}
      />

      {/* Multiple Stop Load */}
      <MultipleStopSection
        formData={formData}
        handleInputChange={handleInputChange}
      />

      {/* Additional Info */}
      <AdditionalInfoSection
        formData={formData}
        handleInputChange={handleInputChange}
      />

      {/* Fees */}
      <FeesSection
        formData={formData}
        handleInputChange={handleInputChange}
      />

      {/* Important Shipment & Remarks */}
      <RemarksSection
        formData={formData}
        handleInputChange={handleInputChange}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
            <ArrowUpFromLine className="h-6 w-6 text-green-400" />
            Exports
          </h2>
          <p className="text-slate-400 text-sm">Manage outbound shipments and bookings</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={copyContainerNumbers}
            className="border-slate-700 whitespace-nowrap"
            data-testid="export-copy-containers-btn"
            title="Copy container numbers"
          >
            <Copy className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Copy #s</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatusOrderModalOpen(true)}
            className="border-slate-700 whitespace-nowrap"
            data-testid="export-status-order-btn"
          >
            <ListOrdered className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Status Order</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchExports}
            className="border-slate-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => {
              setFormData(getInitialFormState());
              setEditingExport(null);
              setShowAddModal(true);
            }}
            className="bg-green-600 hover:bg-green-500"
            data-testid="add-export-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <ExportsFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        sslFilter={sslFilter}
        onSslFilterChange={setSslFilter}
        driverFilter={driverFilter}
        onDriverFilterChange={setDriverFilter}
        customerFilter={customerFilter}
        onCustomerFilterChange={setCustomerFilter}
        statusOptions={EXPORT_STATUSES}
        sslOptions={masterData.ssls}
        driverOptions={masterData.drivers}
        customerOptions={customers}
        hasActiveFilters={statusFilter || sslFilter || driverFilter || customerFilter || searchTerm}
        onClearFilters={() => { setStatusFilter(''); setSslFilter(''); setDriverFilter(''); setCustomerFilter(''); setSearchTerm(''); }}
        onRefresh={() => fetchExports(currentPage)}
        includeClosed={includeClosed}
        onIncludeClosedChange={setIncludeClosed}
        hiddenStatuses={hiddenStatuses}
        onHiddenStatusesChange={setHiddenStatuses}
        sortColumn={sortColumn}
        onClearSort={clearSort}
        columnsChanged={columnsChanged}
        savingColumns={savingColumns}
        onSaveColumns={() => saveColumnPreferences(columns)}
        onOpenColumnSettings={() => setColumnSettingsOpen(true)}
        selectedCount={selectedCount}
        onOpenBulkStatus={() => setBulkStatusModalOpen(true)}
      />

      {/* Exports Table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-0">
          <ExportsTable
            exports={filteredExports}
            loading={loading}
            columns={columns}
            statusColors={STATUS_COLORS}
            statuses={EXPORT_STATUSES}
            masterData={masterData}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onColumnSort={handleColumnSort}
            isAllSelected={isAllSelected}
            isSomeSelected={isSomeSelected}
            toggleSelectAll={toggleSelectAll}
            isSelected={isSelected}
            toggleSelectExport={toggleSelectExport}
            getRowBackgroundColor={getRowBackgroundColor}
            lastEditedExportId={lastEditedExportId}
            onToggleImportant={toggleImportant}
            onOpenDispatch={handleOpenDispatch}
            onEditExport={handleEditExport}
            onOpenStatusChange={handleOpenStatusChange}
            onDeleteExport={openDeleteModal}
            onStatusChange={handleStatusChange}
            onCellColorChange={handleCellColorChange}
            onInlineDateSave={handleInlineDateSave}
            onChassisSave={handleChassisSave}
            onPPCTFSave={handlePPCTFSave}
            inlineEditCell={inlineEditCell}
            inlineEditValue={inlineEditValue}
            setInlineEditValue={setInlineEditValue}
            startInlineEdit={startInlineEdit}
            handleInlineEditSave={handleInlineEditSave}
            cancelInlineEdit={cancelInlineEdit}
            draggedColumn={draggedColumn}
            dragOverColumn={dragOverColumn}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onResizeStart={handleResizeStart}
            formatDate={formatDate}
            formatTime={formatTime}
            getCellTextColor={getCellTextColor}
            tableRef={tableRef}
          />
          
          {/* Pagination */}
          {!loading && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalExports}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              itemLabel="exports"
            />
          )}
        </CardContent>
      </Card>

      {/* Status Color Legend - Click to filter */}
      <StatusColorLegend
        statuses={EXPORT_STATUSES}
        statusColors={statusColors}
        defaultColors={DEFAULT_STATUS_ROW_COLORS}
        statusCounts={statusCounts}
        activeFilter={statusFilter}
        onFilterChange={setStatusFilter}
        onCustomizeClick={() => setColorSettingsOpen(true)}
        showCustomize={user?.role === 'admin'}
      />

      {/* Column Settings Modal */}
      <ColumnSettingsModal
        open={columnSettingsOpen}
        onOpenChange={setColumnSettingsOpen}
        columns={columns}
        onSave={saveColumnPreferences}
      />

      {/* Bulk Status Update Modal - Using extracted component */}
      <BulkStatusModal
        open={bulkStatusModalOpen}
        onOpenChange={setBulkStatusModalOpen}
        selectedCount={selectedCount}
        statuses={EXPORT_STATUSES}
        selectedStatus={bulkStatus}
        onStatusChange={setBulkStatus}
        onSubmit={handleBulkStatusChange}
        processing={bulkUpdating}
        entityType="Exports"
      />

      {/* Dispatch Modal */}
      <Dialog open={dispatchModalOpen} onOpenChange={setDispatchModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-green-400" />
              Send Dispatch
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Select task type, driver, and schedule for this export dispatch
            </DialogDescription>
          </DialogHeader>
          {dispatchingExport && (
            <ExportDispatchScheduleTab
              exportData={dispatchingExport}
              drivers={masterData?.drivers || []}
              terminals={masterData?.terminals || []}
              yards={masterData?.yards || []}
              api={api}
              onDispatchCreated={() => {
                setDispatchModalOpen(false);
                setDispatchingExport(null);
                fetchExports(currentPage);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Change Location/Status Modal - Using LocationChangeModal like Imports */}
      <LocationChangeModal
        open={statusChangeModalOpen}
        onOpenChange={setStatusChangeModalOpen}
        shipment={statusChangeExport}
        terminals={masterData?.terminals || []}
        yards={masterData?.yards || []}
        drivers={masterData?.drivers || []}
        onUpdate={() => {
          fetchExports();
          setStatusChangeModalOpen(false);
          setStatusChangeExport(null);
        }}
        api={api}
        entityType="export"
      />

      {/* Add Export Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpFromLine className="h-5 w-5 text-green-400" />
              New Export Booking
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new outbound export shipment
            </DialogDescription>
          </DialogHeader>

          {renderFormContent(false)}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => closeModal(false)}
              className="border-slate-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveExport}
              disabled={saving}
              className="bg-green-600 hover:bg-green-500"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Export Sheet (with tabs for Documents, Dispatch Schedule, Notes, Status History) */}
      <EditExportSheet
        open={showEditModal}
        onOpenChange={(open) => {
          setShowEditModal(open);
          if (!open) {
            // Refresh dashboard so any inline saves made INSIDE the sheet
            // (status changes, document uploads, etc.) reflect immediately.
            fetchExports(currentPage);
          }
        }}
        exportData={editingExport}
        masterData={masterData}
        customers={customers}
        onSave={(updatedExport) => {
          setExports(prev => prev.map(exp => 
            exp.id === updatedExport.id ? { ...exp, ...updatedExport } : exp
          ));
          setLastEditedExportId(updatedExport.id);
          setTimeout(() => setLastEditedExportId(null), 3000);
          setEditingExport(null);
        }}
        onDelete={(exportId) => {
          setExports(prev => prev.filter(exp => exp.id !== exportId));
          setEditingExport(null);
        }}
      />

      {/* Add Customer Modal - Using extracted component */}
      <AddCustomerModal
        open={showAddCustomerModal}
        onOpenChange={setShowAddCustomerModal}
        customerName={newCustomerName}
        onCustomerNameChange={setNewCustomerName}
        onSubmit={handleAddCustomer}
        processing={addingCustomer}
      />

      {/* iter 264d.15b — fuzzy-match dedup dialog */}
      <CustomerFuzzyMatchDialog
        open={exportsFuzzyOpen}
        onOpenChange={setExportsFuzzyOpen}
        attemptedName={newCustomerName}
        matches={exportsFuzzyMatches}
        onLink={handleLinkExportsCustomer}
        onCreateAnyway={() => _doAddCustomer(newCustomerName.trim())}
      />

      {/* Delete Confirmation Modal - Using extracted component */}
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onOpenChange={(open) => {
          setDeleteModalOpen(open);
          if (!open) setDeleteExport(null);
        }}
        title="Delete Export"
        description="Are you sure you want to delete this export? This action cannot be undone."
        item={deleteExport}
        displayFields={[
          { label: 'Booking #', key: 'booking_number', mono: true },
          { label: 'Container', key: 'container_number', mono: true },
          { label: 'Customer', key: 'customer_name' }
        ]}
        requirePin={false}
        onConfirm={handleDeleteExport}
        loading={deletingExport}
        confirmText="Delete Export"
      />

      {/* Status Color Settings */}
      <StatusColorSettings
        open={colorSettingsOpen}
        onOpenChange={setColorSettingsOpen}
        currentColors={statusColors}
        onSave={(newColors) => {
          setStatusColors(newColors);
        }}
        preferencesKey="export_status_colors"
      />

      {/* Status Order Modal */}
      <StatusOrderModal
        open={statusOrderModalOpen}
        onOpenChange={setStatusOrderModalOpen}
        statusOrder={statusOrder}
        onSave={saveStatusOrder}
      />
    </div>
  );
};

export default ExportsDashboard;
